import { Badge } from '@/admin/components/ui/badge';
import type { TraceEvent } from '../../api';
import { fallbackEventText } from '../../trace/parse-payload';
import type { TracePresentationEvent } from '../../trace/presentation';
import {
  formatCount,
  formatDateTime,
  formatMs,
  statusVariant,
} from '../../format';

function hasWarningQuality(value?: string) {
  return Boolean(value && value !== 'exact');
}

export function TimelineEventRow({
  item,
  selectedEventID,
  onSelect,
}: {
  item: TracePresentationEvent;
  selectedEventID: string;
  onSelect: (event: TracePresentationEvent) => void;
}) {
  const event = item.event;
  return (
    <button
      type="button"
      className={`w-full rounded-md border p-2.5 text-left hover:bg-muted ${
        event.id === selectedEventID ? 'border-primary bg-muted' : ''
      }`}
      onClick={() => onSelect(item)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge variant="outline" className="h-5 px-1.5 text-xs">
            {event.kind.replace('_', ' ')}
          </Badge>
          {item.collapsed ? (
            <Badge variant="outline" className="h-5 px-1.5 text-xs">
              auxiliary match
            </Badge>
          ) : null}
          <span className="font-medium leading-5">
            {event.title || event.kind}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(event.at)} · {formatMs(event.duration_ms)}
          </span>
          {hasWarningQuality(event.quality) ? (
            <Badge variant="outline" className="h-5 px-1.5 text-xs">
              quality {event.quality}
            </Badge>
          ) : null}
        </div>
        <Badge variant={statusVariant(event.status)} className="shrink-0">
          {event.status}
        </Badge>
      </div>
      <TimelineEventBody item={item} />
    </button>
  );
}

function TimelineEventBody({ item }: { item: TracePresentationEvent }) {
  const event = item.event;
  const summary = timelineSummary(item);
  const meta = eventMetaItems(event);
  if (!summary && meta.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1">
      {meta.length > 0 ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {meta.map(item => (
            <span key={item.label}>
              {item.label}:{' '}
              <span className="text-foreground/80">{item.value}</span>
            </span>
          ))}
        </div>
      ) : null}
      {summary ? (
        <div className="line-clamp-2 whitespace-pre-wrap break-words text-sm text-muted-foreground">
          {summary}
        </div>
      ) : null}
    </div>
  );
}

function timelineSummary(item: TracePresentationEvent) {
  const parsed = item.parsed;
  if (parsed.kind === 'assistant_tool_calls') {
    const tools = parsed.toolCalls.map(tool => tool.name).join(', ');
    return parsed.text || (tools ? `Tool calls: ${tools}` : '');
  }
  if (parsed.kind === 'tool_result_message') {
    return parsed.toolCallId
      ? `Tool result for ${parsed.toolCallId}`
      : 'Tool result mirror';
  }
  if (parsed.kind === 'assistant_text') return parsed.text;
  if (parsed.kind === 'tool_call') {
    const text = fallbackEventText(item.event);
    return text || [parsed.toolName, parsed.error].filter(Boolean).join(' ');
  }
  return fallbackEventText(item.event);
}

function eventMetaItems(event: TraceEvent) {
  const details = event.details || {};
  switch (event.kind) {
    case 'tool_call':
      return [
        metaItem('tool', details.tool_name),
        metaItem('input', details.input_ref ? 'available' : undefined),
        metaItem('output', details.output_ref ? 'available' : undefined),
      ].filter(Boolean) as { label: string; value: string }[];
    case 'llm_call':
      return [
        metaItem(
          'model',
          [details.provider, details.model].filter(Boolean).join(' ')
        ),
        metaItem('ttft', optionalMs(numberDetail(details.ttft_ms))),
        metaItem('tokens', optionalCount(numberDetail(details.output_tokens))),
      ].filter(Boolean) as { label: string; value: string }[];
    case 'user_message':
    case 'assistant_message':
      return [
        metaItem('message', details.message_id),
        metaItem('tokens', optionalCount(numberDetail(details.tokens))),
      ].filter(Boolean) as { label: string; value: string }[];
    case 'system_event':
      return [metaItem('event', details.event_type)].filter(Boolean) as {
        label: string;
        value: string;
      }[];
    default:
      return [];
  }
}

function metaItem(label: string, value: unknown) {
  if (value === undefined || value === null || value === '') return null;
  return { label, value: String(value) };
}

function numberDetail(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function optionalMs(value: number | null) {
  return value === null ? '' : formatMs(value);
}

function optionalCount(value: number | null) {
  return value === null ? '' : formatCount(value);
}
